/**
 * The Explorer: the active Git repository's files, and the folders added to the workspace.
 *
 * A click opens a file — a Markdown file in the reader, anything else in the editor. A right-click
 * offers the other: "Edit" for a Markdown file, "Read" to go back. The repository tree follows the
 * repository chosen in the header's Git picker, and reloads when it changes.
 */

import { useEffect, useState, type MouseEvent } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { BookOpen, ChevronDown, ChevronRight, File, FileText, Folder, FolderGit2, FolderPlus, Pencil, RefreshCw, X } from 'lucide-react'
import { useI18n } from '../lib/i18n'
import { useActiveRepository } from '../lib/activeRepository'
import { buildTree, useRepoFiles, type TreeNode } from '../lib/repoSource'
import { isDesktop, repoRead, repoWrite, workspaceAdd, workspaceFolders, workspaceList, workspaceRead, workspaceRemove, workspaceWrite } from '../lib/desktop'
import { useFloating } from './useFloating'
import type { OpenFile } from '../lib/files'
import { isMarkdown } from '../lib/markdownFile'
import { onOpenRequest, takePendingOpen } from '../lib/openProjects'

export type OpenAs = 'read' | 'edit'


export function Explorer({ onOpen }: { onOpen: (file: OpenFile, as: OpenAs) => void }) {
  const { t } = useI18n()
  const queryClient = useQueryClient()
  const { active } = useActiveRepository()
  const { data: repo, isFetching } = useRepoFiles(active)
  const [open, setOpen] = useState<Set<string>>(new Set())
  const [menu, setMenu] = useState<{ x: number; y: number; open: (as: OpenAs) => void; markdown: boolean } | null>(null)
  const [problem, setProblem] = useState<string | null>(null)
  const { data: folders } = useQuery({ queryKey: ['workspace-folders'], queryFn: workspaceFolders, enabled: isDesktop() })

  // A file clicked in the sidebar's open projects opens here, as a click in this tree would.
  useEffect(() => {
    const take = () => {
      const request = takePendingOpen()
      if (request) onOpen(request.file, request.as)
    }
    take()
    return onOpenRequest(take)
  }, [onOpen])

  const toggle = (path: string) => setOpen((current) => {
    const next = new Set(current)
    if (next.has(path)) next.delete(path)
    else next.add(path)
    return next
  })

  /** Opens a file of the repository, reading it from the folder on this machine. */
  const openRepoFile = (node: TreeNode, as: OpenAs) => {
    setProblem(null)
    if (!repo?.folder) {
      setProblem(t('explorer.webReadOnly'))
      return
    }
    const folder = repo.folder
    repoRead(folder, node.path)
      .then((content) => onOpen({ name: node.name, content, path: `${folder}/${node.path}`, writer: (text) => repoWrite(folder, node.path, text) }, as))
      .catch((error) => setProblem(String(error).includes('not-text') ? t('editor.error.not-text') : String(error).includes('too-large') ? t('editor.error.too-large') : t('editor.error.failed')))
  }

  const context = (event: MouseEvent, name: string, openAs: (as: OpenAs) => void) => {
    event.preventDefault()
    setMenu({ x: event.clientX, y: event.clientY, open: openAs, markdown: isMarkdown(name) })
  }

  const renderRepo = (nodes: TreeNode[], depth: number): JSX.Element => (
    <ul className="explorer__list">
      {nodes.map((node) =>
        node.children ? (
          <li key={node.path}>
            <button type="button" className="explorer__row" style={{ paddingLeft: 6 + depth * 12 }} onClick={() => toggle(node.path)}>
              {open.has(node.path) ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
              <Folder size={13} /> <span className="truncate">{node.name}</span>
            </button>
            {open.has(node.path) && renderRepo(node.children, depth + 1)}
          </li>
        ) : (
          <li key={node.path}>
            <button
              type="button"
              className="explorer__row explorer__row--file"
              style={{ paddingLeft: 18 + depth * 12 }}
              onClick={() => openRepoFile(node, isMarkdown(node.name) ? 'read' : 'edit')}
              onContextMenu={(event) => context(event, node.name, (as) => openRepoFile(node, as))}
              title={node.path}
            >
              {isMarkdown(node.name) ? <FileText size={13} /> : <File size={13} />} <span className="truncate">{node.name}</span>
            </button>
          </li>
        ),
      )}
    </ul>
  )

  return (
    <aside className="explorer" aria-label={t('explorer.title')}>
      <div className="explorer__head">
        <strong className="tiny">{t('explorer.title')}</strong>
        <span className="spacer" />
        <button type="button" className="btn btn--ghost btn--icon btn--sm" title={t('explorer.reload')} aria-label={t('explorer.reload')}
          onClick={() => void queryClient.invalidateQueries({ queryKey: ['repo-files'] })}>
          <RefreshCw size={12} className={isFetching ? 'spin' : undefined} />
        </button>
      </div>

      <section className="explorer__section">
        <div className="explorer__title tiny">
          <FolderGit2 size={12} /> {active ? active.name : t('explorer.noRepo')}
          {repo?.branch && <span className="muted mono"> · {repo.branch}</span>}
        </div>
        {active && repo && !repo.live && (
          <p className="tiny muted explorer__note">{repo.at ? t('explorer.snapshot', { at: new Date(repo.at).toLocaleString() }) : t('explorer.noSnapshot')}</p>
        )}
        {repo && renderRepo(buildTree(repo.files), 0)}
      </section>

      {isDesktop() && (
        <section className="explorer__section">
          <div className="explorer__title tiny">
            <Folder size={12} /> {t('explorer.workspace')}
            <span className="spacer" />
            <button type="button" className="btn btn--ghost btn--icon btn--sm" title={t('explorer.addFolder')} aria-label={t('explorer.addFolder')}
              onClick={() => void workspaceAdd().then(() => queryClient.invalidateQueries({ queryKey: ['workspace-folders'] }))}>
              <FolderPlus size={13} />
            </button>
          </div>
          {(folders ?? []).length === 0 && <p className="tiny muted explorer__note">{t('explorer.addFolderHint')}</p>}
          {(folders ?? []).map((folder) => (
            <WorkspaceFolder key={folder.path} path={folder.path} name={folder.name} depth={0}
              onRemove={() => void workspaceRemove(folder.path).then(() => queryClient.invalidateQueries({ queryKey: ['workspace-folders'] }))}
              onOpen={onOpen} onContext={context} onProblem={setProblem} />
          ))}
        </section>
      )}

      {problem && <p className="tiny explorer__problem" role="alert">{problem}</p>}

      {menu && <FileMenu x={menu.x} y={menu.y} markdown={menu.markdown} onPick={(as) => { setMenu(null); menu.open(as) }} onClose={() => setMenu(null)} />}
    </aside>
  )
}

/** A workspace folder, listed lazily as it is opened. Also drawn by the sidebar's open projects. */
export function WorkspaceFolder({ path, name, depth, onRemove, onOpen, onContext, onProblem }: {
  path: string
  name: string
  depth: number
  onRemove?: () => void
  onOpen: (file: OpenFile, as: OpenAs) => void
  onContext: (event: MouseEvent, name: string, open: (as: OpenAs) => void) => void
  onProblem: (message: string | null) => void
}) {
  const { t } = useI18n()
  const [expanded, setExpanded] = useState(false)
  const { data: entries } = useQuery({ queryKey: ['workspace-list', path], queryFn: () => workspaceList(path), enabled: expanded })

  const openFile = (file: { name: string; path: string }, as: OpenAs) => {
    onProblem(null)
    workspaceRead(file.path)
      .then((content) => onOpen({ name: file.name, content, path: file.path, writer: (text) => workspaceWrite(file.path, text) }, as))
      .catch((error) => onProblem(String(error).includes('not-text') ? t('editor.error.not-text') : t('editor.error.failed')))
  }

  return (
    <div>
      <div className="explorer__folder-row">
        <button type="button" className="explorer__row" style={{ paddingLeft: 6 + depth * 12 }} onClick={() => setExpanded(!expanded)} title={path}>
          {expanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
          <Folder size={13} /> <span className="truncate">{name}</span>
        </button>
        {onRemove && (
          <button type="button" className="btn btn--ghost btn--icon btn--sm" onClick={onRemove} title={t('explorer.removeFolder')} aria-label={t('explorer.removeFolder')}>
            <X size={11} />
          </button>
        )}
      </div>
      {expanded && (
        <ul className="explorer__list">
          {(entries ?? []).map((entry) =>
            entry.dir ? (
              <li key={entry.path}>
                <WorkspaceFolder path={entry.path} name={entry.name} depth={depth + 1} onOpen={onOpen} onContext={onContext} onProblem={onProblem} />
              </li>
            ) : (
              <li key={entry.path}>
                <button type="button" className="explorer__row explorer__row--file" style={{ paddingLeft: 18 + (depth + 1) * 12 }}
                  onClick={() => openFile(entry, isMarkdown(entry.name) ? 'read' : 'edit')}
                  onContextMenu={(event) => onContext(event, entry.name, (as) => openFile(entry, as))}
                  title={entry.path}>
                  {isMarkdown(entry.name) ? <FileText size={13} /> : <File size={13} />} <span className="truncate">{entry.name}</span>
                </button>
              </li>
            ),
          )}
        </ul>
      )}
    </div>
  )
}

/** Right-click on a file: Read (Markdown) or Edit. */
export function FileMenu({ x, y, markdown, onPick, onClose }: { x: number; y: number; markdown: boolean; onPick: (as: OpenAs) => void; onClose: () => void }) {
  const { t } = useI18n()
  const { ref, position } = useFloating(x, y, onClose)
  return (
    <div ref={ref} className="bug-menu" role="menu" style={{ left: position.left, top: position.top }} onContextMenu={(event) => event.preventDefault()}>
      {markdown && (
        <button type="button" role="menuitem" className="bug-menu__item" onClick={() => onPick('read')}>
          <BookOpen size={14} aria-hidden /> {t('explorer.read')}
        </button>
      )}
      <button type="button" role="menuitem" className="bug-menu__item" onClick={() => onPick('edit')}>
        <Pencil size={14} aria-hidden /> {t('explorer.edit')}
      </button>
    </div>
  )
}
